import * as React from 'react'

import { clsx } from 'clsx'

import {
  Avatar,
  ChannelIcon,
  CircleCheckBigIcon,
  DropdownMenu,
  EllipsisIcon,
  Flex,
  IconButton,
  Text,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { Sidepanel } from '../../components/Sidepanel'
import { isChannelIconService } from '../../util/isChannelIconService'
import type { ConversationCardData } from '../fragments/conversationCard'
import type { DMsChannel } from '../types'
import {
  formatShortTime,
  getConversationState,
  getCounterpart,
  getPreviewLine,
} from './conversationDisplay'
import type { PreviewLine } from './conversationDisplay'

import styles from './ConversationCard.module.css'

export type ConversationCardProps = {
  conversation: ConversationCardData
  /**
   * The channel the conversation happens on, for the card's channel line. The
   * conversation node carries only the channel id and network, so the name
   * comes from the page's channel list; without it the line shows the
   * network icon alone.
   */
  channel: DMsChannel | undefined
  isSelected: boolean
  onSelect: (conversationId: string) => void
  onResolve: (conversationId: string) => void
  onUnresolve: (conversationId: string) => void
  /** Clock for the age label; tests and stories pin it. */
  now?: Date
}

const PREVIEW_TONE_CLASS: Record<PreviewLine['tone'], string> = {
  new: styles.previewNew,
  unreplied: styles.previewUnreplied,
  replied: styles.previewReplied,
  resolved: styles.previewResolved,
  failed: styles.previewFailed,
}

/**
 * One card of the conversation list, per the DMs Figma `_DM card`:
 *
 *   [network icon] Channel name
 *   [avatar] Counterpart name                       [dot | check | menu]
 *            Newest message or "[n] new messages"   [age]
 *
 * The state reads as weight and colour rather than a label. Unanswered
 * carries a green dot and a neutral, medium-weight preview. Replied ("You: …") recedes
 * to subtle regular text. Resolved dims everything and swaps the dot for a
 * check. Hovering or focusing the card reveals the actions menu in the dot's
 * place, and while it does, the unanswered dot moves down over the age.
 * The menu holds Resolve, or Undo resolve once resolved. Both
 * trailing slots keep a fixed size, so nothing shifts on hover.
 *
 * Hover, focus and selected borders match the by-post posts panel.
 *
 * The card is a native button inside the list item, so Enter and Space
 * select it and screen readers announce it as a control without any ARIA
 * role. The selected card carries `aria-current`. A button may only hold
 * phrasing content, so every layout container inside renders as a `span`.
 *
 * The hover swap is purely visual: the status words ("Unanswered", "Resolved")
 * live outside the swapped slot so the button's accessible name keeps them
 * whether or not the card is hovered or focused, and the age is only made
 * transparent, never removed.
 *
 * A button may not hold another control, so the menu's trigger is the
 * card's sibling in the list item, laid over the trailing slot. It is
 * focusable on its own, right after the card in tab order. The reply-window
 * badge on the avatar is deliberately absent while its design is being
 * tested with users.
 */
export const ConversationCard = React.forwardRef<
  HTMLLIElement,
  ConversationCardProps
>(function ConversationCard(
  { conversation, channel, isSelected, onSelect, onResolve, onUnresolve, now },
  ref,
) {
  const counterpart = getCounterpart(conversation.participants)
  const state = getConversationState(conversation)
  const preview = getPreviewLine(conversation)
  // `||`, not `??`: an empty display name should fall through to the handle.
  const channelName = channel?.displayName || channel?.name
  const service = conversation.serviceType

  const select = (): void => onSelect(conversation.id)

  const statusMark =
    state === 'unreplied' ? (
      <span className={styles.dot} aria-hidden />
    ) : state === 'resolved' ? (
      <CircleCheckBigIcon
        size="xsmall"
        className={styles.resolvedIcon}
        aria-hidden
      />
    ) : null
  const statusWord =
    state === 'unreplied'
      ? 'Unanswered'
      : state === 'resolved'
        ? 'Resolved'
        : null

  return (
    <Sidepanel.ListItem ref={ref} selected={isSelected} className={styles.item}>
      <button
        type="button"
        aria-current={isSelected ? 'true' : undefined}
        onClick={select}
        className={clsx(
          styles.card,
          state === 'resolved' && styles.resolved,
          state === 'unreplied' && styles.unanswered,
        )}
      >
        <Flex as="span" align="center" gap="2xs" className={styles.shrinkable}>
          {isChannelIconService(service) && (
            <ChannelIcon type={service} size="xsmall" color="subtle" />
          )}
          {channelName && (
            <Text
              size="sm"
              weight="medium"
              color="subtle"
              lineHeight="tight"
              className={styles.truncated}
            >
              {channelName}
            </Text>
          )}
        </Flex>
        <Flex as="span" align="center" gap="xs" className={styles.shrinkable}>
          <Avatar
            src={counterpart.avatar}
            alt=""
            fallback="letter"
            fallbackName={counterpart.fallbackName}
            size="mid"
            className={styles.avatar}
          />
          <Flex as="span" direction="column" gap="3xs" className={styles.text}>
            <Flex as="span" align="center" justify="between" gap="xs" fullWidth>
              <Text
                weight="medium"
                lineHeight="tight"
                className={clsx(styles.truncated, styles.grow)}
              >
                {counterpart.name}
              </Text>
              {statusWord && <VisuallyHidden>{statusWord}</VisuallyHidden>}
              <Flex
                as="span"
                align="center"
                justify="center"
                className={styles.mark}
              >
                <span className={styles.markIdle}>{statusMark}</span>
              </Flex>
            </Flex>
            <Flex as="span" align="center" justify="between" gap="xs" fullWidth>
              <Text
                lineHeight="tight"
                className={clsx(
                  styles.truncated,
                  styles.grow,
                  PREVIEW_TONE_CLASS[preview.tone],
                )}
              >
                {preview.text}
              </Text>
              <Text size="sm" color="subtle" className={styles.age}>
                <time
                  dateTime={conversation.lastActivityAt}
                  className={styles.ageTime}
                >
                  {formatShortTime(conversation.lastActivityAt, now)}
                </time>
                {state === 'unreplied' && (
                  <span
                    className={clsx(styles.dot, styles.ageDot)}
                    aria-hidden
                  />
                )}
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </button>
      <ConversationCardMenu
        counterpartName={counterpart.name}
        isResolved={state === 'resolved'}
        onResolve={(): void => onResolve(conversation.id)}
        onUnresolve={(): void => onUnresolve(conversation.id)}
      />
    </Sidepanel.ListItem>
  )
})

type ConversationCardMenuProps = {
  counterpartName: string
  isResolved: boolean
  onResolve: () => void
  onUnresolve: () => void
}

/** The card's actions, the same as the thread header's minus the details. */
function ConversationCardMenu({
  counterpartName,
  isResolved,
  onResolve,
  onUnresolve,
}: ConversationCardMenuProps): React.JSX.Element {
  return (
    <span className={styles.menu}>
      <DropdownMenu>
        <DropdownMenu.Trigger asChild>
          <IconButton
            label={`Actions for the conversation with ${counterpartName}`}
            variant="tertiary"
            size="small"
          >
            <EllipsisIcon />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {isResolved ? (
            <DropdownMenu.Item onSelect={onUnresolve}>
              <CircleCheckBigIcon />
              Undo resolve
            </DropdownMenu.Item>
          ) : (
            <DropdownMenu.Item onSelect={onResolve}>
              <CircleCheckBigIcon />
              Resolve
            </DropdownMenu.Item>
          )}
        </DropdownMenu.Content>
      </DropdownMenu>
    </span>
  )
}
